import {
  GetSanitizedAndDeletedCasesQuery,
  ReportStatus,
} from '../../../graphql/generated';
import { CaseStatus, DeletedSanitizedCase } from './types';

type SanitizedOrDeletedCase =
  GetSanitizedAndDeletedCasesQuery['sanitizedAndDeletedCases'][number];

interface FilterParams {
  searchQuery: string;
  typeFilter: string;
  countryFilter: string;
}

const CASE_STATUS_LABELS: Record<ReportStatus, string> = {
  [ReportStatus.Draft]: 'Draft',
  [ReportStatus.Lodged]: 'Lodged',
  [ReportStatus.New]: 'New',
  [ReportStatus.Read]: 'Read',
  [ReportStatus.Investigating]: 'Investigating',
  [ReportStatus.Closed]: 'Closed',
};

export const transformCase = (
  caseData: SanitizedOrDeletedCase,
): DeletedSanitizedCase => {
  const status = caseData.sanitizedAt
    ? CaseStatus.SANITIZED
    : CaseStatus.DELETED;
  const retentionRule = caseData.RetentionRule;
  const conditions = retentionRule?.conditions as
    | { duration?: number; unit?: string }
    | undefined;
  const retentionPeriod =
    conditions?.duration !== undefined && conditions?.unit
      ? `${conditions.duration} ${conditions.unit}`
      : 'Immediate';

  return {
    id: caseData.id,
    title: caseData.name || caseData.readableId || `Case ${caseData.id}`,
    status,
    metadata: {
      originalCaseId: caseData.readableId || caseData.id,
      appliedPolicy: retentionRule
        ? `${retentionRule.country || 'N/A'} — ${retentionPeriod} ${retentionRule.ruleType || 'N/A'}`
        : 'Unknown',
      caseStatus: CASE_STATUS_LABELS[caseData.status] ?? caseData.status,
      caseClosed:
        caseData.status === ReportStatus.Closed && caseData.closedAt
          ? new Date(caseData.closedAt).toLocaleDateString()
          : undefined,
      anonymizedOn: caseData.sanitizedAt
        ? new Date(caseData.sanitizedAt).toLocaleDateString()
        : undefined,
      deletedOn: caseData.deletedAt
        ? new Date(caseData.deletedAt).toLocaleDateString()
        : undefined,
      substantiation: caseData.substantiation ?? 'N/A',
      retentionPeriod,
      country: retentionRule?.country || 'N/A',
    },
  };
};

export const filterCases = (
  cases: DeletedSanitizedCase[],
  { searchQuery, typeFilter, countryFilter }: FilterParams,
): DeletedSanitizedCase[] =>
  cases.filter((c) => {
    if (typeFilter && c.status !== (typeFilter as CaseStatus)) return false;
    if (countryFilter && c.metadata.country !== countryFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        c.title.toLowerCase().includes(q) ||
        c.metadata.originalCaseId.toLowerCase().includes(q)
      );
    }
    return true;
  });
